const MAX_RESULTS = 8;

function normalizeText(value = '') {
	return value
		.toString()
		.toLowerCase()
		.replace(/(\d)['’]s/g, '$1s')
		.replace(/[^a-z0-9\s]/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

function getEntryLabel(entry) {
	if (entry.category === 'Number') return entry.number || entry.title;

	return (entry.number || entry.title || '')
		.replace(/^repeating\s+/i, '')
		.replace(/['’]/g, '')
		.trim();
}

function getBaseEntryMeta(entry) {
	if (entry.category === 'Number') return entry.subCategory || 'Number';
	if (entry.category === 'Repeating Sequences') return 'Repeating Sequence';
	return 'Mixed Sequence';
}

function getThemes(entry) {
	return Array.isArray(entry.themes) ? entry.themes : [];
}

function getMatchedTheme(entry, query) {
	return getThemes(entry).find((theme) => {
		const label = normalizeText(theme.label);
		const terms = Array.isArray(theme.terms) ? theme.terms.map(normalizeText) : [];
		return label.includes(query) || terms.some((term) => term.includes(query) || query.includes(term));
	});
}

function getEntryMeta(entry, query) {
	const base = getBaseEntryMeta(entry);
	const theme = getMatchedTheme(entry, query);
	return theme ? `${base} - ${theme.label}` : base;
}

function getSequenceDigits(entry) {
	const aliases = Array.isArray(entry.aliases) ? entry.aliases : [];
	const examples = Array.isArray(entry.exampleSequences) ? entry.exampleSequences : [];
	const source = [entry.number || entry.title || '', ...aliases, ...examples].join(' ');
	return [...new Set(source.match(/\d/g) || [])];
}

function getSearchAliases(entry) {
	const aliases = Array.isArray(entry.aliases) ? entry.aliases : [];
	const examples = Array.isArray(entry.exampleSequences) ? entry.exampleSequences : [];
	return [...aliases, ...examples].map(normalizeText);
}

function themeScore(entry, query) {
	let score = -1;
	getThemes(entry).forEach((theme, index) => {
		const label = normalizeText(theme.label);
		const terms = Array.isArray(theme.terms) ? theme.terms.map(normalizeText) : [];
		const positionWeight = index * 15;

		if (label === query) score = Math.max(score, 640 - positionWeight);
		else if (label.startsWith(query)) score = Math.max(score, 590 - positionWeight);
		else if (label.includes(query)) score = Math.max(score, 540 - positionWeight);

		if (terms.some((term) => term === query)) score = Math.max(score, 560 - positionWeight);
		else if (terms.some((term) => term.includes(query))) score = Math.max(score, 500 - positionWeight);
	});
	return score;
}

function scoreEntry(entry, query) {
	if (!query) return -1;

	const label = normalizeText(entry.label);
	const title = normalizeText(entry.title);
	const number = normalizeText(entry.number);
	const isNumericQuery = /^\d+$/.test(query);
	const queryDigits = isNumericQuery ? [...new Set(query.split(''))] : [];
	const sequenceDigits = Array.isArray(entry.sequenceDigits) ? entry.sequenceDigits : [];
	const aliases = getSearchAliases(entry);
	let score = themeScore(entry, query);

	if (entry.category === 'Number' && isNumericQuery) {
		if (number === query) score = Math.max(score, 1000);
		else if (number.startsWith(query)) score = Math.max(score, 700 - Math.max(number.length - query.length, 0));
		else if (number.includes(query)) score = Math.max(score, 400);
	}

	if (
		entry.category === 'Mixed Sequences' &&
		isNumericQuery &&
		queryDigits.length >= 2 &&
		sequenceDigits.length >= queryDigits.length
	) {
		if (aliases.includes(query)) score = Math.max(score, queryDigits.length === 2 ? 800 : 760);
		else if (queryDigits.every((digit) => sequenceDigits.includes(digit))) {
			score = Math.max(score, queryDigits.length === 2 ? 600 : 560);
		}
	}

	if (
		entry.category === 'Repeating Sequences' &&
		isNumericQuery &&
		queryDigits.length === 1 &&
		sequenceDigits[0] === queryDigits[0]
	) {
		score = Math.max(score, 550);
	}

	if (label === query) score = Math.max(score, 900);
	else if (label.startsWith(query)) score = Math.max(score, 650);
	else if (label.includes(query)) score = Math.max(score, 350);

	if (title === query) score = Math.max(score, 500);
	else if (title.includes(query)) score = Math.max(score, 250);

	if (aliases.some((alias) => alias === query)) score = Math.max(score, 520);
	else if (aliases.some((alias) => alias.includes(query))) score = Math.max(score, 300);

	return score;
}

export default function homepageSearch() {
	const root = document.querySelector('[data-homepage-search]');
	if (!root || root.dataset.searchInitialized) return;
	root.dataset.searchInitialized = 'true';

	const indexElement = document.querySelector('[data-homepage-search-index]');
	const form = root.querySelector('[data-homepage-search-form]');
	const input = root.querySelector('[data-homepage-search-input]');
	const results = root.querySelector('[data-homepage-search-results]');
	const keypad = root.querySelector('[data-homepage-search-keypad]');
	if (!form || !input || !results || !keypad) return;

	let entries = null;
	let loadPromise = null;
	let filteredEntries = [];
	let activeIndex = -1;
	let currentQuery = '';

	const clearResults = () => {
		filteredEntries = [];
		activeIndex = -1;
		results.innerHTML = '';
		results.hidden = true;
		results.setAttribute('role', 'listbox');
	};

	const renderStatus = (message) => {
		filteredEntries = [];
		activeIndex = -1;
		results.innerHTML = '';
		results.setAttribute('role', 'status');
		const status = document.createElement('p');
		status.className = 'homepage-search__status';
		status.textContent = message;
		results.append(status);
		results.hidden = false;
	};

	const updateActiveResult = () => {
		results.querySelectorAll('[data-result-index]').forEach((link, index) => {
			const isActive = index === activeIndex;
			link.classList.toggle('is-active', isActive);
			link.setAttribute('aria-selected', isActive ? 'true' : 'false');
		});
	};

	const renderResults = () => {
		results.innerHTML = '';
		results.setAttribute('role', 'listbox');
		if (!filteredEntries.length) {
			renderStatus('No matching number or source theme.');
			return;
		}

		const fragment = document.createDocumentFragment();
		filteredEntries.forEach((entry, index) => {
			const link = document.createElement('a');
			const label = document.createElement('span');
			const meta = document.createElement('span');
			link.href = entry.url;
			link.className = 'homepage-search__result';
			link.dataset.resultIndex = String(index);
			link.setAttribute('role', 'option');
			link.setAttribute('aria-selected', 'false');
			label.className = 'homepage-search__result-label';
			label.textContent = entry.label;
			meta.className = 'homepage-search__result-meta';
			meta.textContent = getEntryMeta(entry, currentQuery);
			link.append(label, meta);
			link.addEventListener('mousemove', () => {
				activeIndex = index;
				updateActiveResult();
			});
			fragment.append(link);
		});
		results.append(fragment);
		results.hidden = false;
		updateActiveResult();
	};

	const loadEntries = () => {
		if (entries) return Promise.resolve(entries);
		if (loadPromise) return loadPromise;

		loadPromise = (async () => {
			let data = [];
			if (indexElement) data = JSON.parse(indexElement.textContent || '[]');
			else if (root.dataset.homepageSearchIndexUrl) {
				const response = await fetch(root.dataset.homepageSearchIndexUrl);
				if (!response.ok) throw new Error(`Failed to load search index: ${response.status}`);
				data = await response.json();
			}
			entries = data.map((entry) => ({
				...entry,
				label: getEntryLabel(entry),
				sequenceDigits: getSequenceDigits(entry),
			}));
			return entries;
		})().catch((error) => {
			console.error(error);
			renderStatus('Search is unavailable.');
			return [];
		});
		return loadPromise;
	};

	const search = async (rawQuery) => {
		const query = normalizeText(rawQuery);
		currentQuery = query;
		if (!query) {
			clearResults();
			return;
		}

		const searchableEntries = await loadEntries();
		if (query !== normalizeText(input.value)) return;
		filteredEntries = searchableEntries
			.map((entry) => ({ ...entry, score: scoreEntry(entry, query) }))
			.filter((entry) => entry.score >= 0)
			.sort((left, right) => {
				if (right.score !== left.score) return right.score - left.score;
				if (left.category !== right.category) {
					if (left.category === 'Number') return -1;
					if (right.category === 'Number') return 1;
				}
				return left.label.length - right.label.length;
			})
			.slice(0, MAX_RESULTS);
		activeIndex = -1;
		renderResults();
	};

	const navigateToSelection = () => {
		const target = activeIndex >= 0 ? filteredEntries[activeIndex] : filteredEntries[0];
		if (!target) return;
		if (window.sacredScribesNavigate) window.sacredScribesNavigate(target.url);
		else window.location.assign(target.url);
	};

	form.addEventListener('submit', async (event) => {
		event.preventDefault();
		if (!filteredEntries.length) await search(input.value);
		navigateToSelection();
	});
	input.addEventListener('focus', loadEntries, { once: true });
	input.addEventListener('input', () => void search(input.value));

	keypad.addEventListener('click', (event) => {
		const button = event.target.closest('button');
		if (!button) return;
		const { keypadAction, keypadValue } = button.dataset;
		if (keypadAction === 'clear') {
			input.value = '';
			clearResults();
			return;
		}
		if (keypadAction === 'backspace') input.value = input.value.slice(0, -1);
		else if (typeof keypadValue === 'string') input.value += keypadValue;
		void search(input.value);
	});

	input.addEventListener('keydown', (event) => {
		if (event.key === 'Escape') {
			if (!input.value && results.hidden) return;
			event.preventDefault();
			input.value = '';
			clearResults();
			return;
		}
		if (!filteredEntries.length) return;
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			activeIndex = activeIndex < filteredEntries.length - 1 ? activeIndex + 1 : 0;
			updateActiveResult();
		}
		if (event.key === 'ArrowUp') {
			event.preventDefault();
			activeIndex = activeIndex > 0 ? activeIndex - 1 : filteredEntries.length - 1;
			updateActiveResult();
		}
	});

	results.addEventListener('click', clearResults);
	requestAnimationFrame(() => {
		if (
			input.hasAttribute('data-homepage-search-autofocus') &&
			!window.location.hash &&
			document.activeElement === document.body
		) {
			input.focus({ preventScroll: true });
			input.select();
		}
	});
}
